import React, { useContext, useEffect, useRef } from 'react';
import classes from './DraggableVariation.module.scss';
import { CustomParagraph } from '../../../../models/slate-editor';
import { useDrag, useDrop } from 'react-dnd';
import { Identifier } from 'dnd-core';
import { SlateWrapper } from '../SlateWrapper';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { SlateWrapperViewProps } from '../SlateWrapper/SlateWrapper.view';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';

export type MoveVariationRow = (dragIndex: number, hoverIndex: number) => void;

export type DraggableVariationViewProps = {
  //
  index: number;
  text: CustomParagraph[];
  textId: number;
  moveRow: MoveVariationRow;
  cancelDrop: VoidFunction;
  applyDrop: VoidFunction;
  canDrag: boolean;
  canRemove: boolean;
  isLast: boolean;
  shouldShowHashtagCounter: boolean;
  hashtagsCount: number;
  borderErrorType: SlateWrapperViewProps['borderErrorType'];
  customizeContentPerProfile: boolean;
  addVariation: (id: number) => void;
  addComment: VoidFunction;
  shouldAddComment: boolean;
  showFirstComment: boolean;
  publishNext: boolean;
  isDragging: boolean;
};

export type VariationDndProps = {
  handlerId: Identifier | null;
  hovering: boolean;
};

export type DragVariationItem = {
  index: number;
  text: CustomParagraph[];
  textId: number;
  width: number;
  height: number;
  showFirstComment: boolean;
};

export type DragVariationResult = { isSelfDragging: boolean; item: DragVariationItem };

export const DraggableVariationView = ({
  isDragging,
  index,
  text,
  textId,
  moveRow,
  cancelDrop,
  applyDrop,
  canDrag,
  canRemove,
  isLast,
  shouldShowHashtagCounter,
  hashtagsCount,
  borderErrorType,
  customizeContentPerProfile,
  addVariation,
  addComment,
  shouldAddComment,
  showFirstComment,
  publishNext
}: DraggableVariationViewProps): JSX.Element => {
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  // const ref = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  // const tooltipRef = useRef<Tooltip>(null);

  const [{ isSelfDragging }, drag, preview] = useDrag<DragVariationItem, DragVariationItem, DragVariationResult>({
    type: DraggableTypesObject.TEXT_VARIATION,
    item: () => {
      const { width, height } =
        previewRef.current && previewRef.current.parentElement
          ? previewRef.current.parentElement.getBoundingClientRect()
          : { width: 0, height: 0 };
      return { index, text, textId, width, height, showFirstComment };
    },
    collect: monitor => ({
      isSelfDragging: monitor.isDragging(),
      item: monitor.getItem()
    }),
    end: (_, monitor) => (monitor.didDrop() ? applyDrop() : cancelDrop())
  });

  const [collectedProps, drop] = useDrop<DragVariationItem, void, VariationDndProps>({
    accept: DraggableTypesObject.TEXT_VARIATION,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        hovering: monitor.isOver()
      };
    },
    hover(item: DragVariationItem, monitor) {
      if (!previewRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items since they are the same
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = previewRef.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      // if we don't have offset, abort
      if (!clientOffset) return;

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // preview(getEmptyImage(), { captureDraggingState: true });

      moveRow(dragIndex, hoverIndex); //ONLY FIRING WHEN WE ARE OVER THE HANDLE :(
      // since we moved, we need to mutate the item.index
      item.index = hoverIndex;
    }
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, [preview]);

  drag(dragRef);
  drop(previewRef);
  // drop(preview(previewRef));

  const dragHandler = (
    <>
      <div className={classes.DraggableVariationHandlerWrapper} ref={dragRef}>
        <div className={classes.DraggableVariationHandler} id={`tooltip-for-drag-variation-${textId}`}>
          <Icon name={IconsEnum.dragHandle} size={'medium'} />
        </div>
      </div>
    </>
  );

  return (
    <>
      <SlateWrapper
        isDragging={isDragging}
        isSelfDragging={isSelfDragging}
        ref={previewRef}
        dragableProps={collectedProps}
        dragHandler={canDrag ? dragHandler : null}
        borderErrorType={borderErrorType}
        isLast={isLast}
        {...(shouldShowHashtagCounter ? { hashtagsCount } : {})}
        key={`${
          typeof customizeIndex === 'number' && customizeContentPerProfile ? customizeIndex + '-' : ''
        }slate-${textId}`}
        addVariation={() => {
          addVariation(textId);
        }}
        addComment={
          shouldAddComment && !showFirstComment
            ? () => {
                addComment();
              }
            : undefined
        }
        publishNext={publishNext}
        text={text}
        id={textId}
        canRemove={canRemove}
      />
    </>
  );
};
